import React, { useState, useEffect } from 'react';
import { XMarkIcon, KeyIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from '../utils/toast';

export interface UserEditData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'operator' | 'staff';
  isActive: boolean;
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
}

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, data: Partial<UserEditData>) => Promise<void>;
  onSendPasswordReset: (userId: string) => Promise<void>;
  user: UserEditData | null;
  departments: string[];
  currentUserId?: string;
}

const COMMON_SPECIALTIES = [
  'Internal Medicine',
  'Family Medicine',
  'Pediatrics',
  'Cardiology',
  'Dermatology',
  'Emergency Medicine',
  'Endocrinology',
  'Gastroenterology',
  'Geriatrics',
  'Hematology',
  'Infectious Disease',
  'Nephrology',
  'Neurology',
  'Obstetrics & Gynecology',
  'Oncology',
  'Ophthalmology',
  'Orthopedics',
  'Otolaryngology',
  'Pathology',
  'Physical Medicine',
  'Psychiatry',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Surgery',
  'Urology'
];

export const UserEditModal: React.FC<UserEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onSendPasswordReset,
  user,
  departments,
  currentUserId
}) => {
  const [formData, setFormData] = useState<Partial<UserEditData>>({});
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [showSpecialtySuggestions, setShowSpecialtySuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        isActive: user.isActive,
        profile: {
          title: user.profile?.title || '',
          department: user.profile?.department || '',
          licenseNumber: user.profile?.licenseNumber || '',
          specialties: user.profile?.specialties || [],
          phoneNumber: user.profile?.phoneNumber || ''
        }
      });
      setErrors({});
    }
  }, [user]);

  const isCurrentUser = user?.id === currentUserId;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!user || !validateForm()) return;

    setIsLoading(true);
    try {
      await onSave(user.id, formData);
      toast.success('User updated successfully');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user) return;

    setIsSendingReset(true);
    try {
      await onSendPasswordReset(user.id);
      toast.success(`Password reset email sent to ${user.email}`);
      setShowResetConfirm(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to send password reset email');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleAddSpecialty = (specialty: string) => {
    const trimmed = specialty.trim();
    if (trimmed && !formData.profile?.specialties?.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          specialties: [...(prev.profile?.specialties || []), trimmed]
        }
      }));
    }
    setSpecialtyInput('');
    setShowSpecialtySuggestions(false);
  };

  const handleRemoveSpecialty = (specialty: string) => {
    setFormData(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        specialties: prev.profile?.specialties?.filter(s => s !== specialty) || []
      }
    }));
  };

  const filteredSuggestions = COMMON_SPECIALTIES.filter(
    s => s.toLowerCase().includes(specialtyInput.toLowerCase()) &&
      !formData.profile?.specialties?.includes(s)
  );

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Edit User: {user.firstName} {user.lastName}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* Basic Information */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.profile?.phoneNumber || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    profile: { ...prev.profile, phoneNumber: e.target.value }
                  }))}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Professional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.profile?.title || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      profile: { ...prev.profile, title: e.target.value }
                    }))}
                    placeholder="e.g., MD, RN, NP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={formData.profile?.department || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      profile: { ...prev.profile, department: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select department...</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Number
                </label>
                <input
                  type="text"
                  value={formData.profile?.licenseNumber || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    profile: { ...prev.profile, licenseNumber: e.target.value }
                  }))}
                  placeholder="e.g., MD123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specialties
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={specialtyInput}
                    onChange={(e) => {
                      setSpecialtyInput(e.target.value);
                      setShowSpecialtySuggestions(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (specialtyInput.trim()) {
                          handleAddSpecialty(specialtyInput);
                        }
                      }
                    }}
                    onFocus={() => setShowSpecialtySuggestions(true)}
                    placeholder="Type to add specialty..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {showSpecialtySuggestions && specialtyInput && filteredSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredSuggestions.slice(0, 8).map(specialty => (
                        <button
                          key={specialty}
                          type="button"
                          onClick={() => handleAddSpecialty(specialty)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                        >
                          {specialty}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formData.profile?.specialties && formData.profile.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.profile.specialties.map(specialty => (
                      <span
                        key={specialty}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                      >
                        {specialty}
                        <button
                          type="button"
                          onClick={() => handleRemoveSpecialty(specialty)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Account Status */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Account Status</h3>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    checked={formData.isActive === true}
                    onChange={() => setFormData(prev => ({ ...prev, isActive: true }))}
                    disabled={isCurrentUser}
                    className="form-radio h-4 w-4 text-indigo-600 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    checked={formData.isActive === false}
                    onChange={() => setFormData(prev => ({ ...prev, isActive: false }))}
                    disabled={isCurrentUser}
                    className="form-radio h-4 w-4 text-red-600 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Inactive</span>
                </label>
              </div>
              {isCurrentUser && (
                <p className="mt-1 text-xs text-gray-500">
                  You cannot deactivate your own account
                </p>
              )}
            </div>

            {/* Password Reset */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Password Reset</h3>
              <p className="text-sm text-gray-500 mb-3">
                Send a password reset email to allow the user to set a new password.
              </p>
              {!showResetConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={!formData.isActive}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <KeyIcon className="h-4 w-4 mr-2" />
                  Send Password Reset
                </button>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center text-amber-600">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm">Send reset email to {user.email}?</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleSendPasswordReset}
                    disabled={isSendingReset}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isSendingReset ? 'Sending...' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {!formData.isActive && (
                <p className="mt-2 text-xs text-amber-600">
                  Cannot send password reset to inactive users
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
